import { Icon } from '@tameeni/icons'
import { Button, Card, Popup, PopupContent, PopupFooter } from '@tameeni/ui'
import { useGetExpiringPolicies } from '../queries'
import { useDisclosure } from '@tameeni/shared-logic'
import { useCallback, useEffect, useState } from 'react'
import { useCurrentLocale, useScopedI18n } from '@/locales/client'
import { useLocation } from 'react-router-dom'
import { useNavigate } from '@/hooks/use-navigate'
import { formatDate } from '@/lib/utils'
import { useAuth } from 'react-oidc-context'
import { STORAGE_KEYS } from '@/lib/constants'

export default function RenewalNotificationPopup() {
  const locale = useCurrentLocale()
  const t = useScopedI18n('features.policyRenewal')
  const tCommon = useScopedI18n('common.words')
  const [isFetchEnabled, setIsFetchEnabled] = useState<boolean>(false)
  const { isAuthenticated } = useAuth()
  const {
    data: expiringPolicies,
    isLoading,
    refetch: refetchExpiringPolicies,
  } = useGetExpiringPolicies({
    enabled: isFetchEnabled && isAuthenticated,
  })
  const policyRenewalData = expiringPolicies?.policies?.[0]
  const { isOpened, onOpen, setIsOpened, onClose } = useDisclosure({
    isDefaultOpened: false,
  })
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname

  const onConfirm = () => {
    onClose()
    navigate(
      `/${locale}/policy-renewal?applicationReference=${policyRenewalData?.applicationReference}`
    )
  }

  const checkExpiringPoliciesInStorage = useCallback(() => {
    const justLoggedIn = localStorage.getItem(
      STORAGE_KEYS.CHECK_EXPIRING_POLICIES
    )
    if (
      justLoggedIn === 'true' &&
      isAuthenticated &&
      !pathname.includes('internal-login')
    ) {
      localStorage.removeItem(STORAGE_KEYS.CHECK_EXPIRING_POLICIES)
      if (isFetchEnabled) {
        refetchExpiringPolicies()
      } else {
        setIsFetchEnabled(true)
      }
    }
  }, [isAuthenticated, pathname])

  useEffect(() => {
    if (policyRenewalData) {
      onOpen()
    }
  }, [policyRenewalData, onOpen])

  useEffect(() => {
    // Add event listener
    globalThis.window.addEventListener(
      'storage',
      checkExpiringPoliciesInStorage
    )

    // Cleanup function to remove the event listener
    return () => {
      globalThis.window.removeEventListener(
        'storage',
        checkExpiringPoliciesInStorage
      )
    }
  }, [checkExpiringPoliciesInStorage])

  useEffect(() => {
    checkExpiringPoliciesInStorage()
  }, [pathname, status, checkExpiringPoliciesInStorage])

  if (!policyRenewalData || isLoading) return null

  return (
    <Popup
      open={isOpened}
      onOpenChange={(value) => {
        if (!value) {
          setIsFetchEnabled(false)
        }
        setIsOpened(value)
      }}
    >
      <PopupContent className='flex flex-col items-center'>
        <div className='flex flex-col items-center gap-1'>
          <Icon name='TravelCoverage' className='mb-2 size-16' />
          <h1 className='text-s1 text-slate-900'>
            {t('policyRenewalPopupTitle', {
              policyHolderName:
                locale === 'ar'
                  ? policyRenewalData?.policyHolderNameAr
                  : policyRenewalData?.policyHolderNameEn,
              expiryDate: formatDate(policyRenewalData?.expiryDate),
            })}
          </h1>
          <p className='text-s2 text-red-500'>
            {t('daysRemaining', {
              days: policyRenewalData?.daysUntilExpiry ?? 0,
            })}
          </p>
          <h3 className='text-s2 text-slate-500'>
            {t('policyRenewalPopupDescription')}
          </h3>
          <Card className='mt-2 w-full p-4'>
            <div className='flex items-center gap-3'>
              <Card className='flex size-12 items-center justify-center'>
                <Icon name='Login' className='size-full text-blue-600' />
              </Card>
              <h3 className='flex flex-1 text-s2 text-slate-900'>
                {policyRenewalData?.insuranceCompanyName}
              </h3>
              <p className='text-s3 text-slate-400'>
                {t('expiryDate')}:{' '}
                <span className='text-red-500'>
                  {formatDate(policyRenewalData?.expiryDate)}
                </span>
              </p>
            </div>
          </Card>
        </div>
        <PopupFooter className='flex-col gap-2'>
          <Button
            endIcon={
              <Icon name='ArrowRight' className='size-4 rtl:rotate-180' />
            }
            loading={isLoading}
            onClick={onConfirm}
            data-testid='renewal-proceed-btn'
          >
            {t('proceedToRenewal')}
          </Button>
          <Button
            variant='outline'
            onClick={onClose}
            data-testid='renewal-cancel-btn'
          >
            {tCommon('cancel')}
          </Button>
        </PopupFooter>
      </PopupContent>
    </Popup>
  )
}
